import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { useLeaveGroup } from '../../hooks/useConnections';

interface LeaveGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Connection to leave — must be a QR-campaign connection (qrCodeId set). */
  connectionId: string;
  /** Group label shown in the modal body ("Leave the group '{group}'."). */
  groupName: string;
}

/**
 * "Leave Group" modal for a marketing-QR campaign connection. Two radio
 * options let the caller either preserve their existing group connections
 * (keep) or purge them all (remove). Both branches flip
 * marketing_data.disable_group_connections server-side so future scanners
 * don't reconnect the caller. Continue stays disabled until a radio is
 * picked — that click IS the confirmation, no second "Are you sure?" step.
 *
 * Rendered above the existing modals in the connections screen (z-[70]) so
 * the popover-triggered flow doesn't get covered by an outer sheet.
 */
export const LeaveGroupModal: React.FC<LeaveGroupModalProps> = ({
  isOpen,
  onClose,
  connectionId,
  groupName,
}) => {
  const { t } = useTranslation('connections');
  const leave = useLeaveGroup();
  const [choice, setChoice] = useState<'keep' | 'remove' | null>(null);

  // Reset the choice every time the modal reopens — a stale prior selection
  // would let the user Continue without an active tap on this open.
  useEffect(() => {
    if (isOpen) setChoice(null);
  }, [isOpen]);

  const handleContinue = async () => {
    if (!choice || leave.isPending) return;
    try {
      await leave.mutateAsync({
        id: connectionId,
        removeConnections: choice === 'remove',
      });
      onClose();
    } catch {
      // Server errors surface via the global error modal (same as the
      // create/delete flows). Keep this modal open so the user can retry.
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" zIndexClass="z-[70]">
      <h2 className="mb-2 text-lg font-bold text-foreground">
        {t('leaveGroup.title')}
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">
        {t('leaveGroup.body', { group: groupName })}
      </p>
      <p className="mb-2 text-sm font-medium text-foreground">
        {t('leaveGroup.chooseOne')}
      </p>
      {/* Only the verb (keep / remove) is bolded + brand-colored — the rest
          of each label stays neutral. The <1>…</1> placeholder in the i18n
          value scopes the emphasis to that word so each locale can wrap
          its own translation (Spanish → conservar / eliminar). */}
      <div className="mb-4 flex flex-col gap-2">
        <label className="flex items-start gap-2 cursor-pointer text-sm text-foreground">
          <input
            type="radio"
            name="leave-group-choice"
            value="keep"
            checked={choice === 'keep'}
            onChange={() => setChoice('keep')}
            className="mt-1"
          />
          <span>
            <Trans
              i18nKey="leaveGroup.keepConnections"
              t={t}
              components={{
                1: <span className="font-bold text-brand-primary" />,
              }}
            />
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer text-sm text-foreground">
          <input
            type="radio"
            name="leave-group-choice"
            value="remove"
            checked={choice === 'remove'}
            onChange={() => setChoice('remove')}
            className="mt-1"
          />
          <span>
            <Trans
              i18nKey="leaveGroup.removeConnections"
              t={t}
              components={{
                1: <span className="font-bold text-brand-primary" />,
              }}
            />
          </span>
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={leave.isPending}
          className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted disabled:opacity-50 transition-colors"
        >
          {t('leaveGroup.cancel')}
        </button>
        <button
          type="button"
          onClick={handleContinue}
          disabled={!choice || leave.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
        >
          {t('leaveGroup.continue')}
        </button>
      </div>
    </Modal>
  );
};
